import { IPromise, IQService, IRootScopeService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import colors from "wwwroot/json/colors.json";
import icons from "wwwroot/json/icons.json";
import { CfMap } from "@gtmhub/customFields";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ICroppedTeamPicture, ITeam, TeamService } from "@gtmhub/teams";
import { TeamEvents } from "../events";

interface ITeamFormStateParamsService {
  returnState: string;
  cancelState: string;
  teamId: string;
  "avatar-selector-only": boolean; // ajs router query params can only be dash-case
  parentTeamId?: string;
}
export interface ITeamFormScope extends IModalScope {
  indicators: {
    loadingTeam: boolean;
    saving: boolean;
    showSuccessModal: boolean;
  };

  isEdit: boolean;

  avatarSelector: {
    isOpen: boolean;
    showPreview: boolean;
    isCreateSymbol: boolean;
    setTabUploadPictureActive: boolean;
  };

  error: string;
  team: ITeam;
  teams: ITeam[];
  icons: string[];
  colors: string[];
  selectedParent: ITeam;
  requiredCfNotEmpty: boolean;

  imgData: {
    // this holds the blob with the cropped image
    croppedDataUrl: string;
    croppedDataForSend: string;
    pictureFile: File;
  };

  teamParentId: string[];
  teamManagerId: string[];
  teamAlreadyExists: boolean;
  checkIfTeamAlreadyExists(): void;
  cancel(): void;
  save(): void;
  setColor(color: string): void;
  setAvatar(avatar: string): void;
  setCroppedDataForSend(croppedData: string): void;
  toggleAvatarSelectorTabHeading(heading: string): void;
  updateCustomFields(customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectParentTeamId(ids: string[]): void;
  clearParentTeamId(): void;
  selectManager(ids: string[]): void;
  clearTeamManagerId(): void;
  selectMembers(ids: string[]): void;
  resetAvatar(): void;
  avatarSelectorApply(): void;
  avatarSelectorCancel(): void;
  avatarSelectorOnly: boolean;
  disableAvatarSelectorApplyBtn: boolean;
}

export class TeamFormCtrl {
  public static $inject = ["$rootScope", "$scope", "$state", "$stateParams", "TeamService", "$q", "UIErrorHandlingService"];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: ITeamFormScope,
    private $stateService: StateService,
    private $stateParams: ITeamFormStateParamsService,
    private teamService: TeamService,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.teamEvents = new TeamEvents(this.$scope);
    this.init();
  }

  private teamEvents: TeamEvents;

  private init = () => {
    this.$scope.indicators = {
      loadingTeam: false,
      saving: false,
      showSuccessModal: false,
    };

    // extracted form a query param to persist the avatar selector only state on back navigation and reload
    this.$scope.avatarSelectorOnly = this.$stateParams["avatar-selector-only"];

    this.$scope.avatarSelector = {
      isOpen: this.$scope.avatarSelectorOnly,
      showPreview: false,
      isCreateSymbol: true,
      setTabUploadPictureActive: false,
    };

    this.$scope.imgData = {
      croppedDataUrl: "",
      croppedDataForSend: "",
      pictureFile: null,
    };
    this.$scope.colors = colors.avatarSelectorColors;
    this.$scope.icons = icons;

    this.$scope.cancel = this.cancel;
    this.$scope.save = this.save;
    this.$scope.setColor = this.setColor;
    this.$scope.setAvatar = this.setAvatar;
    this.$scope.toggleAvatarSelectorTabHeading = this.toggleAvatarSelectorTabHeading;
    this.$scope.setCroppedDataForSend = this.setCroppedDataForSend;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.selectParentTeamId = this.selectParentTeamId;
    this.$scope.clearParentTeamId = this.clearParentTeamId;
    this.$scope.selectManager = this.selectManager;
    this.$scope.clearTeamManagerId = this.clearTeamManagerId;
    this.$scope.selectMembers = this.selectMembers;
    this.$scope.resetAvatar = this.resetAvatar;
    this.$scope.checkIfTeamAlreadyExists = this.checkIfTeamAlreadyExists;
    this.$scope.teamAlreadyExists = false;
    this.$scope.avatarSelectorCancel = this.avatarSelectorCancel;
    this.$scope.avatarSelectorApply = this.avatarSelectorApply;
    this.$scope.disableAvatarSelectorApplyBtn = true;

    if (this.$stateParams.teamId) {
      this.$scope.isEdit = true;
      this.loadTeam();
    } else {
      this.$scope.isEdit = false;
      // to do: remove this TS cheat when refactoring
      // id is mandatory in ITeam so we need to create a new interface for a new team but this involves some refactoring
      this.$scope.team = <ITeam>{
        name: "",
        description: "",
        color: "#607293",
        avatar: "travel_world",
        customFields: {},
      };
    }

    this.$scope.teamParentId = [];
    this.$scope.teamManagerId = [];

    if (this.$stateParams.parentTeamId) {
      this.$scope.teamParentId.push(this.$stateParams.parentTeamId);
      this.selectParentTeamId(this.$scope.teamParentId);
    }
  };

  private loadTeam = () => {
    this.$scope.indicators.loadingTeam = true;

    this.teamService.getTeam(this.$stateParams.teamId).then(
      (team) => {
        this.$scope.team = team;
        this.$scope.indicators.loadingTeam = team.parentId ? true : false;
        this.$scope.team.customFields = this.$scope.team.customFields || {};

        if (team.picture) {
          this.toggleAvatarSelectorTabHeading("picture");
          this.$scope.avatarSelector.setTabUploadPictureActive = true;
          this.$scope.avatarSelector.showPreview = true;
        }

        if (team.avatar) {
          this.$scope.avatarSelector.showPreview = true;
        }

        if (team.parentId) {
          this.teamService.getTeam(team.parentId).then(
            (parentTeam) => {
              this.$scope.indicators.loadingTeam = false;
              this.$scope.selectedParent = parentTeam;

              if (!this.$scope.avatarSelector.isOpen) {
                setTimeout(() => document.getElementById("team-name-field")?.focus());
              }
            },

            (error) => this.handleIndicatorError(error, { indicator: "loadingTeam", inline: false })
          );
        } else {
          setTimeout(() => document.getElementById("team-name-field")?.focus());
        }
      },

      (error) => this.handleIndicatorError(error, { indicator: "loadingTeam", inline: false })
    );
  };

  private setColor = (color) => {
    this.$scope.team.color = color;
    this.$scope.disableAvatarSelectorApplyBtn = false;
  };

  private setAvatar = (avatar) => {
    this.$scope.team.avatar = avatar;
    this.$scope.disableAvatarSelectorApplyBtn = false;
    // Remove custom image to update the avatar preview
    this.$scope.team.picture = "";
    this.$scope.imgData.pictureFile = null;
  };

  private setCroppedDataForSend = (croppedData: string): void => {
    // this method is to avoid a bug from library "ng-img-crop"
    if (this.$scope.avatarSelector.isOpen) {
      this.$scope.imgData.croppedDataForSend = croppedData;
    }
  };

  private toggleAvatarSelectorTabHeading = (heading: string) => {
    switch (heading) {
      case "symbol":
        this.$scope.avatarSelector.isCreateSymbol = true;
        this.$scope.imgData.pictureFile = null;
        break;
      case "picture":
        this.$scope.avatarSelector.isCreateSymbol = false;
        break;
    }
  };

  private selectParentTeamId = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.teamParentId = ids;
    this.$scope.team.parentId = ids[0];
  };

  private clearParentTeamId = (): void => {
    this.$scope.teamParentId = [];
    this.$scope.team.parentId = null;
  };

  private selectManager = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.teamManagerId = ids;
    this.$scope.team.manager = ids[0];
  };

  private clearTeamManagerId = (): void => {
    this.$scope.teamManagerId = [];
    this.$scope.team.manager = null;
  };

  private selectMembers = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.team.members = ids;
  };

  private save = (): void => {
    this.uploadPicture().then(
      () => {
        if (this.$stateParams.teamId) {
          this.updateTeam();
        } else {
          this.createTeam();
        }
      },
      (error) => this.handleIndicatorError(error, { indicator: "saving", inline: true })
    );
  };

  private cancel = (): void => {
    const params = this.$stateParams.teamId ? { teamId: this.$stateParams.teamId } : {};
    this.$stateService.go("^.^", params);
  };

  private avatarSelectorCancel = (): void => {
    if (this.$scope.avatarSelectorOnly) {
      this.cancel();
    } else {
      this.$scope.avatarSelector.isOpen = !this.$scope.avatarSelector.isOpen;
    }
  };

  private avatarSelectorApply = (): void => {
    if (this.$scope.avatarSelectorOnly) {
      this.$scope.indicators.saving = true;
      this.uploadPicture()
        .then(() =>
          this.teamService.updateTeam({
            id: this.$scope.team.id,
            avatar: this.$scope.team.avatar,
            color: this.$scope.team.color,
            picture: this.$scope.team.picture,
          })
        )
        .then((team) => {
          this.teamEvents.broadcastTeamUpdated({ team });
          this.cancel();
        })
        .catch((error) => {
          this.handleIndicatorError(error, { indicator: "saving", inline: true });
          this.$scope.indicators.saving = false;
        });
    } else {
      this.$scope.avatarSelector.isOpen = !this.$scope.avatarSelector.isOpen;
      this.$scope.avatarSelector.showPreview = !this.$scope.avatarSelector.showPreview;
    }
  };

  private updateTeam = (): Promise<void> => {
    this.$scope.indicators.saving = true;

    const payloadPartialTeam: ITeam = {
      id: this.$scope.team.id,
      avatar: this.$scope.team.avatar,
      color: this.$scope.team.color,
      picture: this.$scope.team.picture,
      description: this.$scope.team.description,
      name: this.$scope.team.name,
      parentId: this.$scope.team.parentId,
      customFields: this.$scope.team.customFields,
    };

    return this.teamService.updateTeam(payloadPartialTeam).then(
      (result) => {
        this.$scope.error = null;
        this.teamEvents.broadcastTeamUpdated({ team: result });

        this.$scope.indicators.saving = false;
        this.$scope.indicators.showSuccessModal = true;

        this.successModal();
      },

      (error) => this.handleIndicatorError(error, { indicator: "saving", inline: true })
    );
  };

  private createTeam = () => {
    this.$scope.indicators.saving = true;

    this.teamService.createTeam(this.$scope.team).then(
      (result) => {
        this.$scope.error = null;
        this.$rootScope.$broadcast("assigneesChanged", true);

        this.$scope.indicators.saving = false;
        this.$scope.indicators.showSuccessModal = true;
        this.teamEvents.broadcastTeamCreated({ team: result });
        this.$scope.$dismiss();
      },

      (error) => {
        if (error.status === 409) {
          this.$scope.teamAlreadyExists = true;
          this.$scope.indicators.saving = false;
          return;
        }

        this.handleIndicatorError(error, { indicator: "saving", inline: true });
      }
    );
  };

  private checkIfTeamAlreadyExists = (): void => {
    this.$scope.teamAlreadyExists = false;
  };

  private uploadPicture = (): IPromise<void> => {
    this.$scope.indicators.saving = true;

    // if save the form on “Team symbol” the picture must be deleted
    if (this.$scope.avatarSelector.isCreateSymbol) {
      this.$scope.team.picture = "";
      return this.$q.resolve();

      // if there is a picture but changes something else in the form
    } else if (!this.$scope.avatarSelector.isCreateSymbol && !this.$scope.imgData.pictureFile) {
      return this.$q.resolve();
    } else {
      const picture: ICroppedTeamPicture = {
        data: this.$scope.imgData.croppedDataForSend,
        fileName: this.$scope.imgData.pictureFile.name,
      };
      return this.teamService.uploadPicture(picture).then((imgUrl: string) => {
        this.$scope.team.picture = imgUrl;
      });
    }
  };

  private resetAvatar = (): void => {
    this.$scope.team.picture = "";
    this.$scope.team.avatar = "travel_world";
    this.$scope.team.color = "#607293";
    this.$scope.imgData.pictureFile = null;
    this.$scope.avatarSelector.showPreview = !this.$scope.avatarSelector.showPreview;
  };

  private handleIndicatorError = <E>(err: E, params: { indicator: string; inline: boolean }): void => {
    this.uiErrorHandlingService.handleIndicator(err, params);
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.team.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };

  private successModal = (): void => {
    const modalContent = angular.element(document.querySelector(".modal-content"));
    modalContent.css("width", "340px");
  };
}
